// filepath: apps/web/src/editors/form-builder/queries.ts
//
// FR-UI-BLD-1 AC-1 / AC-2 — TanStack Query hooks for the form builder SPA.
//
// Cache keys follow the INV-1 pattern: ['admin', tenantSlug, ...].
// Mutations carry `credentials: 'include'` so the JWT cookie travels.
// The X-Idempotency-Key header is attached to every mutating verb
// (ARCH Rule 3 — idempotency at every entry point).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type {
  EntityTypeSummary,
  FormCreateRequest,
  FormCreateResponse,
  FormulaValidateRequest,
  FormulaValidateResponse,
} from "./types";

export const formBuilderKeys = {
  entityTypes: (tenantSlug: string) =>
    ["admin", tenantSlug, "entity-types"] as const,
  formulaValidate: (tenantSlug: string) =>
    ["admin", tenantSlug, "formula-validate"] as const,
  formSave: (tenantSlug: string) => ["admin", tenantSlug, "form-save"] as const,
};

export function useEntityTypes() {
  const { tenantSlug } = useTenant();
  return useQuery<EntityTypeSummary[]>({
    queryKey: formBuilderKeys.entityTypes(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/entity-types");
      if (!r.ok) {
        throw new Error(`failed to load entity types: ${r.status}`);
      }
      return (await r.json()) as EntityTypeSummary[];
    },
  });
}

export function useFormSave() {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<FormCreateResponse, Error, FormCreateRequest>({
    mutationFn: async (body) => {
      const r = await apiFetch("/api/form-definitions", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        const err = new Error(
          `form save failed: ${r.status} ${JSON.stringify(errBody)}`,
        );
        (err as Error & { body?: unknown; status?: number }).body = errBody;
        (err as Error & { body?: unknown; status?: number }).status = r.status;
        throw err;
      }
      return (await r.json()) as FormCreateResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["admin", tenantSlug, "forms"],
      });
    },
  });
}

export function useFormulaValidate() {
  const { tenantSlug } = useTenant();
  return useMutation<FormulaValidateResponse, Error, FormulaValidateRequest>({
    mutationFn: async (body) => {
      const r = await apiFetch("/api/formula/validate", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      });
      if (!r.ok) {
        throw new Error(`formula validate failed: ${r.status}`);
      }
      return (await r.json()) as FormulaValidateResponse;
    },
    mutationKey: formBuilderKeys.formulaValidate(tenantSlug),
  });
}
